import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getUserDetail, UserDetail, recalculateUser, postSubscriptionGrant, postSubscriptionRevoke } from '../adminApi';
import { useAdmin } from '@/contexts/AdminContext';

export default function AdminUserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { isAuthenticated } = useAdmin();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<UserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [recalcLoading, setRecalcLoading] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const toastRef = useRef<ReturnType<typeof setTimeout>>();

  // Subscription management
  const [subModal, setSubModal] = useState(false);
  const [subAction, setSubAction] = useState<'grant' | 'revoke'>('grant');
  const [subPlan, setSubPlan] = useState('premium');
  const [subDays, setSubDays] = useState(30);
  const [subLoading, setSubLoading] = useState(false);

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    if (toastRef.current) clearTimeout(toastRef.current);
    toastRef.current = setTimeout(() => setToast(null), 3000);
  };

  useEffect(() => {
    if (!isAuthenticated) { navigate('/admin'); return; }
    if (!id) return;
    getUserDetail(id).then(setDetail).catch(() => setDetail(null)).finally(() => setLoading(false));
  }, [id, isAuthenticated, navigate]);

  const handleRecalculate = async () => {
    if (!id) return;
    setRecalcLoading(true);
    try {
      await recalculateUser(id);
      showToast('Расчёт завершён');
      const updated = await getUserDetail(id);
      setDetail(updated);
    } catch (err) {
      showToast('Ошибка: ' + (err instanceof Error ? err.message : 'Неизвестная ошибка'), 'error');
    } finally { setRecalcLoading(false); }
  };

  const handleSubscription = async () => {
    if (!detail) return;
    const user = detail.user as Record<string, any>;
    setSubLoading(true);
    try {
      if (subAction === 'grant') {
        const res = await postSubscriptionGrant({ email: user.email, plan: subPlan, duration_days: subDays });
        showToast(`Подписка ${subPlan} назначена до ${new Date(res.end_date).toLocaleDateString('ru-RU')}`);
      } else {
        await postSubscriptionRevoke({ email: user.email });
        showToast('Подписка отозвана');
      }
      setSubModal(false);
      const updated = await getUserDetail(id!);
      setDetail(updated);
    } catch (err) {
      showToast('Ошибка: ' + (err instanceof Error ? err.message : 'Неизвестная ошибка'), 'error');
    } finally { setSubLoading(false); }
  };

  if (loading) return <div className="min-h-screen bg-slate-900 flex items-center justify-center text-white text-lg">Загрузка...</div>;
  if (!detail) return <div className="min-h-screen bg-slate-900 flex items-center justify-center text-white text-lg">Пользователь не найден</div>;

  const user = detail.user as Record<string, any>;
  const natal = detail.natalChart as Record<string, any> | null;
  const numerology = detail.numerology as Record<string, any> | null;
  const subs = detail.subscriptions as Record<string, any>[];
  const payments = detail.payments as Record<string, any>[];

  const isVerified = Boolean(user.is_verified);

  return (
    <div className="min-h-screen bg-slate-900">
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg text-sm font-medium shadow-lg ${toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}>
          {toast.message}
        </div>
      )}

      <header className="bg-slate-800 border-b border-slate-700 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between flex-wrap gap-3">
          <Link to="/admin/users" className="text-lg font-bold text-white hover:text-purple-400 transition">← Пользователи</Link>
          <h1 className="text-lg font-bold text-white">{user.full_name || user.email}</h1>
          <div className="flex gap-2">
            <button onClick={() => { setSubAction('grant'); setSubModal(true); }}
              className="px-3 py-1.5 text-sm bg-green-600/20 text-green-400 rounded-lg hover:bg-green-600/40 transition">💎 Подписка</button>
            <Link to={`/admin/users/${id}/edit`} className="px-3 py-1.5 text-sm bg-blue-600/20 text-blue-400 rounded-lg hover:bg-blue-600/40 transition">Редактировать</Link>
            <button onClick={handleRecalculate} disabled={recalcLoading}
              className="px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition disabled:opacity-50">
              {recalcLoading ? 'Пересчёт...' : 'Пересчитать'}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {/* Main Info */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-slate-800 rounded-xl border border-slate-700 p-5">
            <h3 className="text-sm font-medium text-slate-400 mb-3">Основная информация</h3>
            <dl className="space-y-2">
              <div><dt className="text-xs text-slate-500">Email</dt><dd className="text-white text-sm">{user.email}</dd></div>
              <div><dt className="text-xs text-slate-500">Имя</dt><dd className="text-white text-sm">{user.full_name || '—'}</dd></div>
              <div><dt className="text-xs text-slate-500">Подписка</dt><dd className="text-white text-sm capitalize">{user.subscription_status || 'free'}</dd></div>
              <div><dt className="text-xs text-slate-500">Верифицирован</dt><dd className="text-sm">{isVerified ? '✅ Да' : '❌ Нет'}</dd></div>
              <div><dt className="text-xs text-slate-500">Регистрация</dt><dd className="text-white text-sm">{user.created_at ? new Date(user.created_at).toLocaleDateString('ru-RU') : '—'}</dd></div>
            </dl>
          </div>

          <div className="bg-slate-800 rounded-xl border border-slate-700 p-5">
            <h3 className="text-sm font-medium text-slate-400 mb-3">Данные рождения</h3>
            <dl className="space-y-2">
              <div><dt className="text-xs text-slate-500">Дата</dt><dd className="text-white text-sm">{user.birth_date || '—'}</dd></div>
              <div><dt className="text-xs text-slate-500">Время</dt><dd className="text-white text-sm">{user.birth_time || '—'}</dd></div>
              <div><dt className="text-xs text-slate-500">Место</dt><dd className="text-white text-sm">{user.birth_place || '—'}</dd></div>
            </dl>
          </div>

          <div className="bg-slate-800 rounded-xl border border-slate-700 p-5">
            <h3 className="text-sm font-medium text-slate-400 mb-3">Активность</h3>
            <dl className="space-y-2">
              <div><dt className="text-xs text-slate-500">Последний вход</dt><dd className="text-white text-sm">{user.last_login_at ? new Date(user.last_login_at).toLocaleDateString('ru-RU') : '—'}</dd></div>
              <div><dt className="text-xs text-slate-500">Подписок</dt><dd className="text-white text-sm">{subs.length}</dd></div>
              <div><dt className="text-xs text-slate-500">Платежей</dt><dd className="text-white text-sm">{payments.length}</dd></div>
            </dl>
          </div>
        </div>

        {/* Natal Chart */}
        {natal && (
          <div className="bg-slate-800 rounded-xl border border-slate-700 p-5">
            <h3 className="text-sm font-medium text-slate-400 mb-4">Натальная карта</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: '☀️ Солнце', value: natal.sun_sign },
                { label: '🌙 Луна', value: natal.moon_sign },
                { label: '⬆️ Асцендент', value: natal.ascendant },
                { label: '☿ Меркурий', value: natal.mercury_sign },
                { label: '♀ Венера', value: natal.venus_sign },
                { label: '♂ Марс', value: natal.mars_sign },
                { label: '♃ Юпитер', value: natal.jupiter_sign },
                { label: '♄ Сатурн', value: natal.saturn_sign },
              ].map(({ label, value }) => (
                <div key={label} className="bg-slate-900/50 rounded-lg p-3">
                  <div className="text-xs text-slate-500">{label}</div>
                  <div className="text-white text-sm font-medium capitalize">{value || '—'}</div>
                </div>
              ))}
            </div>
            {natal.personality_description && (
              <div className="mt-4 p-4 bg-slate-900/50 rounded-lg">
                <div className="text-xs text-slate-500 mb-2">Описание личности</div>
                <div className="text-slate-300 text-sm leading-relaxed">{natal.personality_description}</div>
              </div>
            )}
          </div>
        )}

        {/* Numerology */}
        {numerology && (
          <div className="bg-slate-800 rounded-xl border border-slate-700 p-5">
            <h3 className="text-sm font-medium text-slate-400 mb-4">Нумерология</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: 'Число жизненного пути', value: numerology.life_path_number },
                { label: 'Число судьбы', value: numerology.destiny_number },
                { label: 'Число души', value: numerology.soul_number },
                { label: 'Число личности', value: numerology.personality_number },
              ].map(({ label, value }) => (
                <div key={label} className="bg-slate-900/50 rounded-lg p-3">
                  <div className="text-xs text-slate-500">{label}</div>
                  <div className="text-white text-lg font-bold">{value ?? '—'}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Subscriptions */}
        {subs.length > 0 && (
          <div className="bg-slate-800 rounded-xl border border-slate-700 p-5">
            <h3 className="text-sm font-medium text-slate-400 mb-4">Подписки</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-slate-500 text-xs border-b border-slate-700">
                  <th className="px-3 py-2 text-left">Тариф</th>
                  <th className="px-3 py-2 text-left">Статус</th>
                  <th className="px-3 py-2 text-left">Период</th>
                  <th className="px-3 py-2 text-left">Начало</th>
                  <th className="px-3 py-2 text-left">Конец</th>
                </tr></thead>
                <tbody>
                  {subs.map((s, i) => (
                    <tr key={i} className="border-b border-slate-700/50">
                      <td className="px-3 py-2 text-white capitalize">{s.tier}</td>
                      <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded text-xs ${s.status === 'active' ? 'bg-green-600/30 text-green-300' : 'bg-slate-600 text-slate-300'}`}>{s.status}</span></td>
                      <td className="px-3 py-2 text-slate-400">{s.billing_period}</td>
                      <td className="px-3 py-2 text-slate-400">{s.start_date ? new Date(s.start_date).toLocaleDateString('ru-RU') : '—'}</td>
                      <td className="px-3 py-2 text-slate-400">{s.end_date ? new Date(s.end_date).toLocaleDateString('ru-RU') : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Payments */}
        {payments.length > 0 && (
          <div className="bg-slate-800 rounded-xl border border-slate-700 p-5">
            <h3 className="text-sm font-medium text-slate-400 mb-4">Платежи (последние 50)</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-slate-500 text-xs border-b border-slate-700">
                  <th className="px-3 py-2 text-left">Дата</th>
                  <th className="px-3 py-2 text-left">Сумма</th>
                  <th className="px-3 py-2 text-left">Тариф</th>
                  <th className="px-3 py-2 text-left">Статус</th>
                  <th className="px-3 py-2 text-left">Провайдер</th>
                </tr></thead>
                <tbody>
                  {payments.map((p, i) => (
                    <tr key={i} className="border-b border-slate-700/50">
                      <td className="px-3 py-2 text-slate-400">{new Date(p.created_at).toLocaleDateString('ru-RU')}</td>
                      <td className="px-3 py-2 text-white">{p.amount} {p.currency}</td>
                      <td className="px-3 py-2 text-slate-400 capitalize">{p.tier || '—'}</td>
                      <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded text-xs ${p.status === 'succeeded' ? 'bg-green-600/30 text-green-300' : p.status === 'pending' ? 'bg-amber-600/30 text-amber-300' : 'bg-red-600/30 text-red-300'}`}>{p.status}</span></td>
                      <td className="px-3 py-2 text-slate-400">{p.provider || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      {/* Subscription Modal */}
      {subModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-xl border border-slate-700 p-6 w-full max-w-md">
            <h2 className="text-lg font-bold text-white mb-4">💎 Управление подпиской</h2>
            <p className="text-slate-400 text-sm mb-4">{user.email}</p>
            <div className="flex gap-2 mb-4">
              <button onClick={() => setSubAction('grant')}
                className={`flex-1 py-2 rounded-lg text-sm transition ${subAction === 'grant' ? 'bg-green-600 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}>
                Назначить
              </button>
              <button onClick={() => setSubAction('revoke')}
                className={`flex-1 py-2 rounded-lg text-sm transition ${subAction === 'revoke' ? 'bg-red-600 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}>
                Отозвать
              </button>
            </div>
            {subAction === 'grant' && (
              <div className="space-y-3 mb-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Тариф</label>
                  <select value={subPlan} onChange={(e) => setSubPlan(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500">
                    <option value="base">Base (290 ₽/мес)</option>
                    <option value="premium">Premium (690 ₽/мес)</option>
                    <option value="pro">Pro (990 ₽/мес)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Дней</label>
                  <input type="number" value={subDays} onChange={(e) => setSubDays(parseInt(e.target.value) || 1)} min={1} max={365}
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500" />
                </div>
              </div>
            )}
            <div className="flex gap-3 justify-end">
              <button onClick={() => setSubModal(false)}
                className="px-4 py-2 text-sm bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition">Отмена</button>
              <button onClick={handleSubscription} disabled={subLoading}
                className={`px-4 py-2 text-sm rounded-lg transition disabled:opacity-50 ${subAction === 'grant' ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-red-600 text-white hover:bg-red-700'}`}>
                {subLoading ? 'Выполняется...' : subAction === 'grant' ? 'Назначить' : 'Отозвать'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
